/**
 * Settings → Connected accounts. The systems a person may sign in to with
 * their own account, with where they stand on each and one button: Connect,
 * Disconnect, or Sign in again. Nothing about the connection itself is
 * editable here; that is the admin's Connections area.
 */
import {
  Badge,
  Banner,
  Button,
  Card,
  Skeleton,
  Table,
} from '@servicecompressionllc/sc-design-system';
import { formatDate } from '../../lib/format.js';
import type { McpMyConnection } from '../../lib/types.js';
import { loginProviderName } from '../integrations/shared.js';
import {
  useConnectedAccountsLogic,
  type ConnectedAccountsLogic,
} from './useConnectedAccountsLogic.js';

const StatusBadge = ({ connection }: { connection: McpMyConnection }) => {
  switch (connection.status) {
    case 'connected':
      return <Badge variant="success">Connected</Badge>;
    case 'needs_sign_in':
      return <Badge variant="warning">Needs sign-in</Badge>;
    case 'not_ready':
      return <Badge variant="neutral">Being set up</Badge>;
    default:
      return <Badge variant="neutral">Not connected</Badge>;
  }
};

const statusNote = (connection: McpMyConnection): string => {
  switch (connection.status) {
    case 'connected':
      return connection.signedInAt ? `Signed in ${formatDate(connection.signedInAt)}` : '';
    case 'needs_sign_in':
      return connection.signedInAt
        ? `Your sign-in from ${formatDate(connection.signedInAt)} no longer works`
        : 'Your sign-in no longer works';
    case 'not_ready':
      return 'An admin has not finished setting this up yet';
    default:
      return `Takes you to ${loginProviderName(connection.issuer)} to sign in, then back here`;
  }
};

export const ConnectedAccountsPanel = ({
  logic = useConnectedAccountsLogic(),
}: {
  logic?: ConnectedAccountsLogic;
}) => {
  const { connections, isLoading, error, connect, disconnect, connectingId, disconnectingId } =
    logic;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-ink-muted max-w-[76ch] text-sm leading-relaxed">
        Systems your organization linked to SC Platform that need you to sign in with your own
        account. Only you can see this page. Disconnecting removes your sign-in and nothing else.
      </p>

      {error && (
        <Banner variant="danger">
          <Banner.Body>
            <Banner.Title>Something went wrong.</Banner.Title>
            <Banner.Description>{error.message}</Banner.Description>
          </Banner.Body>
        </Banner>
      )}

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : connections.length === 0 ? (
        <Card padding="lg" className="border-dashed">
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <span className="text-ink text-[15px] font-semibold">Nothing to connect yet</span>
            <span className="text-ink-muted max-w-[52ch] text-sm">
              When your organization links a system that needs a personal sign-in and gives your
              group access, it shows up here.
            </span>
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <Table.Root className="w-full text-sm">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>System</Table.HeaderCell>
                <Table.HeaderCell>Your sign-in</Table.HeaderCell>
                <Table.HeaderCell className="w-44" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {connections.map((connection) => {
                const busy =
                  connectingId === connection.integrationId ||
                  disconnectingId === connection.integrationId;
                return (
                  <Table.Row key={connection.integrationId}>
                    <Table.Cell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-ink text-[15px] font-semibold">
                          {connection.name}
                        </span>
                        <span className="text-ink-muted text-[13px]">
                          Signs in with {loginProviderName(connection.issuer)}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadge connection={connection} />
                        <span className="text-ink-subtle text-xs">{statusNote(connection)}</span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex justify-end">
                        {connection.status === 'connected' ? (
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={busy}
                            onClick={() => void disconnect(connection.integrationId)}
                          >
                            {disconnectingId === connection.integrationId
                              ? 'Disconnecting…'
                              : 'Disconnect'}
                          </Button>
                        ) : connection.status === 'not_ready' ? null : (
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={busy}
                            onClick={() => void connect(connection.integrationId)}
                          >
                            {connectingId === connection.integrationId
                              ? 'Opening sign-in…'
                              : connection.status === 'needs_sign_in'
                                ? 'Sign in again'
                                : 'Connect'}
                          </Button>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        </Card>
      )}

      <p className="text-ink-subtle max-w-[76ch] text-[13px] leading-relaxed">
        You can also connect from the assistant: ask it to connect you to a system, or just use one
        of its tools and it will give you the sign-in link.
      </p>
    </div>
  );
};
